import React, { useRef, useState } from 'react';
import {
  AttentionMessage,
  Box,
  Container,
  DataTable,
  Flex,
  Grid,
  Spinner,
  TableState,
} from '@tq/ui-components';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useIncomingConsultations } from '../../../api';
import { DataTablePagination, SearchPlanningApplications } from '../../../components';
import { useIncomingConsultationsTrafficMonitorColumns } from '../hooks';
import { ReportData } from '../../../types';
import {
  UseIncomingConsultationsTrafficMonitorState,
  incomingConsultationsByReferenceNumberState,
} from '../../../store';

import { GenerateGuidanceList } from './GenerateGuidanceList';

export const IncomingConsultationsMonitorTable: React.FC = () => {
  const { t } = useTranslation('traffic_monitor');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const incomingConsultationsByReferenceNumber = useRecoilValue(
    incomingConsultationsByReferenceNumberState
  );

  const {
    data: incomingConsultationsData,
    isLoading,
    isInitialLoading,
    error,
    status,
    fetchStatus,
    refetch,
  } = useIncomingConsultations({
    enabled: !!incomingConsultationsByReferenceNumber,
    networkMode: 'online',
  });

  const { pageNumberState, pageSizeState, orderByColumnState, orderByDescendingState } =
    UseIncomingConsultationsTrafficMonitorState();
  const columns = useIncomingConsultationsTrafficMonitorColumns(isRefreshing);
  const tableRef = useRef(null);
  const setOrderByColumn = useSetRecoilState(orderByColumnState);
  const setOrderByDescending = useSetRecoilState(orderByDescendingState);

  const handleStateChange = (state: TableState<ReportData>) => {
    const orderByColumn = state.sortBy?.[0]?.id;
    const orderByDescending = state.sortBy?.[0]?.desc ?? false;

    if (orderByColumn) {
      setOrderByColumn(orderByColumn);
      setOrderByDescending(orderByDescending);
    }
  };

  const handleRefetch = () => {
    refetch()
      .then(() => {
        setIsRefreshing(true);
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  return (
    <>
      <Container px={{ base: 0, lg: 4, xl: 0 }} mb={6}>
        <Grid gridGap={4}>
          <AttentionMessage
            status='info'
            title={`${t('response_type_definition_title', 'Guidance')}`}
            description={<GenerateGuidanceList />}
            w='100%'
            maxWidth={{ base: '100%', lg: 'contentWidth' }}
            className='attention-message-small'
          />
          <SearchPlanningApplications
            incomingConsultationsData={incomingConsultationsData}
            refetchData={handleRefetch}
          />
          {isInitialLoading && isLoading ? (
            <Flex justifyContent='center'>
              <Spinner />
            </Flex>
          ) : !incomingConsultationsByReferenceNumber ? (
            <AttentionMessage
              status='info'
              mb={2}
              description={t(
                'pre_search_initial_message',
                'When you have searched for an application using the reference ID, the search results will be shown here'
              )}
            />
          ) : error ||
            (!incomingConsultationsData &&
              status === 'loading' &&
              fetchStatus === 'paused') ? (
            <AttentionMessage
              status='error'
              mb={2}
              description={t(
                'search_function_failure',
                'Search function not working currently, try again'
              )}
            />
          ) : !incomingConsultationsData?.incomingConsultations?.items?.length ? (
            <AttentionMessage
              status='warning'
              mb={2}
              description={t('no_results_found_message', 'No results found')}
            />
          ) : (
            <>
              <Box ref={tableRef} overflow='hidden'>
                <DataTable
                  columns={columns}
                  data={incomingConsultationsData?.incomingConsultations?.items}
                  disableSortRemove
                  disablePagination
                  enableOverflowScrollingContainer
                  onStateChange={handleStateChange}
                  size='sm'
                  sx={{
                    '.tqc-datatable__th-content': {
                      minWidth: '130px',
                    },
                  }}
                  whiteSpace='normal'
                  disableSortBy={true}
                />
              </Box>
              <Box marginBottom={24}>
                <DataTablePagination
                  paginationInfo={incomingConsultationsData?.paginationInfo}
                  pageNumberState={pageNumberState}
                  pageSizeState={pageSizeState}
                  tableRef={tableRef}
                  tableScrollHeaderOffset={167}
                />
              </Box>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
};
